import { Link } from 'react-router-dom';
import { ShieldCheck, Zap, Trophy, ChevronRight, ArrowUpRight, Globe2 } from 'lucide-react';
import { usePageMeta } from '../../../hooks/useSEO';
import BreadcrumbSchema from '../../../components/seo/BreadcrumbSchema';
import { FinalCTA } from './DubaiCostOfLivingPage';

const PILLARS = [
  { icon: ShieldCheck, title: 'Strategic Vetting',     copy: 'Every partner in our 500+ university network passes ZAFCO\u2019s 11-point governance audit — KHDA accreditation, parent-university recognition, AIU-listed degree, transparent fee filings, GTE-friendly intake history, and demonstrable Indian-student outcomes over the last 5 cycles.' },
  { icon: Zap,         title: 'Priority Processing',   copy: 'Direct admissions-desk relationships at 12+ Dubai campuses unlock faster CAS / COE issuance — typical turnaround of 5–10 working days versus the 4–6 weeks an unrepresented applicant faces. We file your application from a counsellor account, not a public form.' },
  { icon: Trophy,      title: 'Exclusive Scholarships', copy: 'Partner-only ZAFCO scholarships at Heriot-Watt, UOWD, Middlesex, S P Jain, Birmingham Dubai and AUD — typically 10–30% of tuition, layered on top of any merit aid. These are not advertised publicly; they\u2019re negotiated cohort by cohort.' },
];

const REGIONS = [
  { name: 'United Kingdom',         count: '120+ universities', highlight: 'Russell Group + post-92 + private specialists' },
  { name: 'Germany',                count: '60+ universities',  highlight: 'TU9 Engineering + Hochschule applied sciences' },
  { name: 'Canada',                 count: '70+ universities',  highlight: 'U15 research + community colleges with PR fast-tracks' },
  { name: 'Australia',              count: '40+ universities',  highlight: 'Go8 + regional CRICOS providers' },
  { name: 'Dubai / UAE',            count: '30+ campuses',      highlight: 'KHDA-licensed branch campuses (UK / US / AUS / IN parent unis)' },
  { name: 'Ireland & New Zealand',  count: '25+ universities',  highlight: 'Stay-back-friendly STEM and healthcare programmes' },
];

const DUBAI_PARTNERS = [
  { name: 'Heriot-Watt University Dubai',          slug: 'heriot-watt-university-dubai-campus' },
  { name: 'American University in Dubai',          slug: 'american-university-in-dubai' },
  { name: 'University of Birmingham Dubai',         slug: 'university-of-birmingham-dubai' },
  { name: 'University of Wollongong Dubai',         slug: 'university-of-wollongong-dubai' },
  { name: 'Middlesex University Dubai',             slug: 'middlesex-university-dubai-campus' },
  { name: 'Hult International Business School',     slug: 'hult-international-business-school-dubai-campus' },
  { name: 'S P Jain School of Global Management',   slug: 'sp-jain-school-of-global-management-dubai-campus' },
  { name: 'Curtin University Dubai',                slug: 'curtin-university-dubai' },
];

export default function DubaiUniversityPartnershipsPage() {
  usePageMeta(
    'ZAFCO Global Partnership Network — 500+ Universities Across 6 Regions',
    'ZAFCO\u2019s strategically-vetted 500+ university partnership network across UK, Germany, Canada, Australia, Ireland and Dubai. Priority CAS/COE processing, exclusive scholarships, and KHDA-accredited Dubai branch campuses.'
  );

  return (
    <div className="zaf-edu-platform bg-white text-slate-900" data-testid="dubai-partnerships-page">
      <BreadcrumbSchema items={[
        { name: 'Home', url: 'https://zafco.in/' },
        { name: 'Study Abroad', url: 'https://zafco.in/study-abroad/' },
        { name: 'Study in Dubai', url: 'https://zafco.in/study-abroad/dubai/' },
        { name: 'University Partnerships', url: 'https://zafco.in/study-abroad/dubai/university-partnerships/' },
      ]} />

      <section className="bg-gradient-to-br from-[#0F1B2C] to-[#162538] text-white py-12 md:py-16">
        <div className="container max-w-5xl mx-auto px-4 sm:px-6 md:px-12">
          <Link to="/study-abroad/dubai/" className="inline-flex items-center gap-2 text-[#D4AF37] text-xs font-semibold uppercase tracking-widest mb-4 hover:text-amber-300">
            <ChevronRight className="w-3 h-3 rotate-180" /> Dubai Hub
          </Link>
          <p className="text-[#D4AF37] text-xs font-bold uppercase tracking-widest mb-2">Global Partnership Network</p>
          <h1 className="text-3xl md:text-5xl font-bold font-syne leading-tight">500+ Universities, <span className="text-[#D4AF37]">Strategically Vetted</span></h1>
          <p className="mt-5 text-slate-300 max-w-3xl leading-relaxed">Quantity is cheap. Quality is engineered. ZAFCO&apos;s partner network is built on three non-negotiables — strict accreditation vetting, priority processing channels, and exclusive student scholarships that aren&apos;t listed on any university website.</p>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="container max-w-6xl mx-auto px-4 sm:px-6 md:px-12">
          <h2 className="text-2xl md:text-3xl font-bold font-syne mb-7">The 3-Pillar Partnership Model</h2>
          <div className="grid md:grid-cols-3 gap-5">
            {PILLARS.map((p) => (
              <article key={p.title} className="bg-slate-50 border border-slate-200 rounded-2xl p-6 hover:border-[#D4AF37] transition-colors" data-testid={`partnership-pillar-${p.title.split(' ')[0].toLowerCase()}`}>
                <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/15 flex items-center justify-center mb-3"><p.icon className="w-6 h-6 text-[#D4AF37]" /></div>
                <h3 className="font-bold font-syne text-[#0F1B2C] mb-2">{p.title}</h3>
                <p className="text-sm text-slate-700 leading-relaxed">{p.copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-slate-50">
        <div className="container max-w-6xl mx-auto px-4 sm:px-6 md:px-12">
          <h2 className="text-2xl md:text-3xl font-bold font-syne mb-3 flex items-center gap-2"><Globe2 className="w-7 h-7 text-[#D4AF37]" /> Network Footprint</h2>
          <p className="text-slate-600 mb-6 max-w-3xl">Region-by-region coverage. Every partner is current as of the 2026 intake cycle and rated by Indian-student satisfaction (last 3 cycles).</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {REGIONS.map((r) => (
              <div key={r.name} className="bg-white border border-slate-200 rounded-2xl p-5 hover:border-[#D4AF37]/60 transition-colors">
                <h3 className="font-bold font-syne text-[#0F1B2C]">{r.name}</h3>
                <p className="text-[#D4AF37] font-bold text-lg font-syne my-1">{r.count}</p>
                <p className="text-xs text-slate-600 leading-relaxed">{r.highlight}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="container max-w-6xl mx-auto px-4 sm:px-6 md:px-12">
          <h2 className="text-2xl md:text-3xl font-bold font-syne mb-3">Featured Dubai Partner Campuses</h2>
          <p className="text-slate-600 mb-6 max-w-3xl">The KHDA-accredited Dubai branch campuses where ZAFCO holds direct admissions-desk relationships. Apply through us for priority CAS/COE and ZAFCO partner scholarships.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {DUBAI_PARTNERS.map((u) => (
              <Link
                key={u.slug}
                to={`/study-abroad/universities/dubai/${u.slug}/`}
                className="group bg-white border border-slate-200 rounded-xl p-4 hover:border-[#D4AF37] hover:shadow-sm transition-all flex items-center justify-between gap-3"
                data-testid={`partner-${u.slug}`}
              >
                <p className="text-sm font-semibold text-[#0F1B2C] group-hover:text-[#0B1322]">{u.name}</p>
                <ArrowUpRight className="w-4 h-4 text-[#D4AF37] flex-shrink-0 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <FinalCTA source="dubai-university-partnerships" />
    </div>
  );
}
